import React from "react";
import {
  Heart,
  MessageCircle,
  Share2,
  MoreHorizontal,
  Bookmark,
  ArrowLeft,
} from "lucide-react";
import styled from "styled-components";
import PollDisplay from "../../components/poll-display";
import LeftSidebar from "../../components/left-sidebar";
import TrendingPanel from "../../components/trending-panel";
import {
  AppLayout,
  MainArea,
  FeedColumn,
  RightSidebar,
  MobileHeader,
  MobileTitle,
  RoleBadge,
  ActionButton,
} from "../../constants/styles";
import { LoggedInUserContext } from "../../../providers/ProvideLoggedInUser";
import { apiGet, apiPost, apiDelete } from "../../../api";
import { renderContent } from "../../utils/render-content";

// Thread Detail Styles
const BackButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--foreground);

  &:hover {
    background: var(--secondary);
  }
`;

const HeaderBar = styled.div`
  display: none;
  align-items: center;
  gap: 1.5rem;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border);
  background: var(--card);
  position: sticky;
  top: 0;
  z-index: 40;

  @media (min-width: 720px) {
    display: flex;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--foreground);
`;

const ThreadContainer = styled.div`
  padding: 1rem;
  border-bottom: 1px solid var(--border);
  background: var(--card);
`;

const ThreadContent = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const AvatarCircle = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), #ffb347);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  text-decoration: none;
  overflow: hidden;

  span {
    font-size: 0.875rem;
    font-weight: 700;
    color: white;
  }
`;

const ThreadMainContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const AuthorHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const AuthorNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AuthorName = styled.a`
  font-weight: 700;
  color: var(--foreground);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const AuthorHandle = styled.span`
  font-size: 0.9375rem;
  color: var(--muted-foreground);
`;

const MoreButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted-foreground);

  &:hover {
    background: var(--secondary);
    color: var(--foreground);
  }
`;

const ThreadText = styled.div`
  margin-top: 0.75rem;
  font-size: 1.0625rem;
  line-height: 1.6;
  color: var(--foreground);
  white-space: pre-wrap;
`;

const ThreadTimestamp = styled.div`
  margin-top: 1rem;
  font-size: 0.9375rem;
  color: var(--muted-foreground);
`;

const ThreadStats = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem 0;
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  display: flex;
  gap: 1.25rem;
  font-size: 0.9375rem;
`;

const StatItem = styled.span`
  color: var(--muted-foreground);

  strong {
    color: var(--foreground);
    font-weight: 700;
  }
`;

const ActionButtons = styled.div`
  padding: 0.5rem 0;
  display: flex;
  justify-content: space-around;
  border-bottom: 1px solid var(--border);
`;

// Reply Section Styles
const ReplyFormContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border);
  background: var(--card);
`;

const FormAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), #ffb347);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  span {
    font-size: 0.875rem;
    font-weight: 700;
    color: white;
  }
`;

const FormContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  border: none;
  background: transparent;
  font-size: 1.125rem;
  color: var(--foreground);
  resize: none;
  outline: none;
  font-family: inherit;

  &::placeholder {
    color: var(--muted-foreground);
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ReplyButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  background: var(--primary);
  color: white;
  font-weight: 700;
  border: none;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ReplyArticle = styled.article`
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  border-bottom: 1px solid var(--border);
  background: var(--card);
`;

const ReplyAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), #ffb347);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  span {
    font-size: 0.875rem;
    font-weight: 700;
    color: white;
  }
`;

const ReplyMainContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ReplyHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const ReplyAuthorLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const ReplyName = styled.span`
  font-weight: 700;
  color: var(--foreground);
`;

const ReplyHandle = styled.span`
  color: var(--muted-foreground);
  font-size: 0.9375rem;
`;

const ReplyText = styled.div`
  margin-top: 0.25rem;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: var(--foreground);
  white-space: pre-wrap;
`;

const ReplyActions = styled.div`
  margin-top: 0.75rem;
  display: flex;
  gap: 3rem;
  margin-left: -0.5rem;
`;

const LoadingState = styled.div`
  padding: 3rem 2rem;
  text-align: center;
  color: var(--muted-foreground);
`;

const ErrorState = styled.div`
  padding: 3rem 2rem;
  text-align: center;
  color: #ef4444;
`;

function ReplyForm({ threadId, onReplied, userInitial }) {
  const [content, setContent] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      const res = await apiPost(`/api/v1/communities/threads/${threadId}/replies`, {
        body: { reply: { content: content.trim() } },
      });
      if (res.statusCode === 201) {
        onReplied(res.parsedResponse.data);
        setContent("");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ReplyFormContainer>
      <FormAvatar>
        <span>{userInitial}</span>
      </FormAvatar>
      <FormContent>
        <Textarea
          placeholder="Post your reply"
          rows={2}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === "Enter") handleSubmit(); }}
        />
        <FormActions>
          <ReplyButton disabled={!content.trim() || submitting} onClick={handleSubmit}>
            {submitting ? "Replying..." : "Reply"}
          </ReplyButton>
        </FormActions>
      </FormContent>
    </ReplyFormContainer>
  );
}

function ReplyCard({ reply, onLike, onSubReplyAdded, userInitial, depth = 0 }) {
  const [showReplyForm, setShowReplyForm] = React.useState(false);
  const [showAllSubReplies, setShowAllSubReplies] = React.useState(false);

  const avatarContent = reply.author.avatar_url ? (
    <img
      src={reply.author.avatar_url}
      alt={reply.author.name}
      style={{ width: "100%", height: "100%", objectFit: "cover" }}
    />
  ) : (
    <span>{reply.author.name?.charAt(0)}</span>
  );

  const [localSubReplies, setLocalSubReplies] = React.useState(null);
  const subReplies = localSubReplies || reply.sub_replies || [];
  const visibleSubReplies = showAllSubReplies ? subReplies : subReplies.slice(0, 2);
  const hiddenCount = subReplies.length - 2;

  const handleSubReplyAdded = (newSubReply) => {
    onSubReplyAdded?.(reply.id, newSubReply);
    setShowReplyForm(false);
  };

  return (
    <div style={{ marginLeft: depth > 0 ? "2rem" : 0 }}>
      <ReplyArticle style={depth > 0 ? { borderLeft: "2px solid var(--border)", paddingLeft: "0.75rem" } : {}}>
        <ReplyAvatar style={{ width: depth > 0 ? 32 : 40, height: depth > 0 ? 32 : 40, flexShrink: 0 }}>
          {avatarContent}
        </ReplyAvatar>
        <ReplyMainContent>
          <ReplyHeader>
            <ReplyAuthorLine>
              <ReplyName>{reply.author.name}</ReplyName>
              {reply.author.role && (
                <RoleBadge>{reply.author.role.split(" ")[0]}</RoleBadge>
              )}
              <ReplyHandle>{reply.author.handle}</ReplyHandle>
              <span style={{ color: "var(--muted-foreground)" }}>·</span>
              <ReplyHandle title={reply.created_at_iso ? new Date(reply.created_at_iso).toLocaleString() : undefined}>{reply.timestamp}</ReplyHandle>
            </ReplyAuthorLine>
            <MoreButton>
              <MoreHorizontal size={16} />
            </MoreButton>
          </ReplyHeader>
          <ReplyText>{renderContent(reply.content)}</ReplyText>
          <ReplyActions>
            <ActionButton onClick={onLike} $isActive={reply.liked} $activeColor="#ef4444">
              <Heart size={16} fill={reply.liked ? "#ef4444" : "none"} />
              {reply.likes > 0 && <span>{reply.likes}</span>}
            </ActionButton>
            <ActionButton onClick={() => setShowReplyForm((v) => !v)} $isActive={showReplyForm}>
              <MessageCircle size={16} />
              {reply.replies > 0 && <span>{reply.replies}</span>}
            </ActionButton>
            <ActionButton onClick={() => navigator.clipboard.writeText(`${window.location.origin}/communities/threads/${reply.id}`)}>
              <Share2 size={16} />
            </ActionButton>
          </ReplyActions>
        </ReplyMainContent>
      </ReplyArticle>

      {/* Inline reply form */}
      {showReplyForm && depth === 0 && (
        <div style={{ marginLeft: "2rem" }}>
          <ReplyForm
            threadId={reply.id}
            onReplied={handleSubReplyAdded}
            userInitial={userInitial}
          />
        </div>
      )}

      {/* Sub-replies */}
      {visibleSubReplies.map((sr) => (
        <ReplyCard
          key={sr.id}
          reply={sr}
          onLike={() => {
            const wasLiked = sr.liked;
            setLocalSubReplies((prev) => (prev || reply.sub_replies || []).map((s) =>
              s.id === sr.id ? { ...s, liked: !wasLiked, likes: wasLiked ? s.likes - 1 : s.likes + 1 } : s
            ));
            const req = wasLiked
              ? apiDelete(`/api/v1/communities/threads/${sr.id}/likes`)
              : apiPost(`/api/v1/communities/threads/${sr.id}/likes`);
            req.catch(() => {
              setLocalSubReplies((prev) => (prev || []).map((s) =>
                s.id === sr.id ? { ...s, liked: wasLiked, likes: sr.likes } : s
              ));
            });
          }}
          depth={1}
          userInitial={userInitial}
        />
      ))}
      {!showAllSubReplies && hiddenCount > 0 && (
        <button
          onClick={() => setShowAllSubReplies(true)}
          style={{ marginLeft: "2rem", background: "none", border: "none", color: "var(--primary)", cursor: "pointer", fontSize: "0.875rem", padding: "0.25rem 0" }}
        >
          View {hiddenCount} more {hiddenCount === 1 ? "reply" : "replies"}
        </button>
      )}
    </div>
  );
}

export default function ThreadDetailPage({ params }) {
  const { currentUser } = React.use(LoggedInUserContext);
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [thread, setThread] = React.useState(null);
  const [replies, setReplies] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const threadId = params?.id;

  React.useEffect(() => {
    if (!threadId) return;
    setLoading(true);
    setError(null);
    apiGet(`/api/v1/communities/threads/${threadId}`)
      .then((res) => {
        if (res.statusCode === 404) {
          setError("Thread not found.");
          return;
        }
        if (res.parsedResponse?.data) {
          const data = res.parsedResponse.data;
          setThread(data);
          setReplies(data.thread_replies || []);
        }
      })
      .catch(() => setError("Failed to load thread."))
      .finally(() => setLoading(false));
  }, [threadId]);

  const updateReply = (replyId, changes) => {
    setReplies((prev) => prev.map((r) => (r.id === replyId ? { ...r, ...changes } : r)));
  };

  const toggleThreadLike = async () => {
    if (!thread) return;
    const wasLiked = thread.liked;
    setThread((t) => ({ ...t, liked: !wasLiked, likes: wasLiked ? t.likes - 1 : t.likes + 1 }));
    if (wasLiked) {
      await apiDelete(`/api/v1/communities/threads/${thread.id}/likes`).catch(() =>
        setThread((t) => ({ ...t, liked: wasLiked, likes: thread.likes }))
      );
    } else {
      await apiPost(`/api/v1/communities/threads/${thread.id}/likes`).catch(() =>
        setThread((t) => ({ ...t, liked: wasLiked, likes: thread.likes }))
      );
    }
  };

  const toggleThreadBookmark = async () => {
    if (!thread) return;
    const wasBookmarked = thread.bookmarked;
    setThread((t) => ({ ...t, bookmarked: !wasBookmarked }));
    if (wasBookmarked) {
      await apiDelete(`/api/v1/communities/threads/${thread.id}/bookmarks`).catch(() =>
        setThread((t) => ({ ...t, bookmarked: wasBookmarked }))
      );
    } else {
      await apiPost(`/api/v1/communities/threads/${thread.id}/bookmarks`).catch(() =>
        setThread((t) => ({ ...t, bookmarked: wasBookmarked }))
      );
    }
  };

  const toggleThreadRepost = async () => {
    if (!thread) return;
    const wasReposted = thread.reposted;
    setThread((t) => ({ ...t, reposted: !wasReposted, reposts: wasReposted ? t.reposts - 1 : t.reposts + 1 }));
    const req = wasReposted
      ? apiDelete(`/api/v1/communities/threads/${thread.id}/reposts`)
      : apiPost(`/api/v1/communities/threads/${thread.id}/reposts`);
    req.catch(() => setThread((t) => ({ ...t, reposted: wasReposted, reposts: thread.reposts })));
  };

  const replyFormRef = React.useRef(null);

  const scrollToReplyForm = () => {
    replyFormRef.current?.scrollIntoView({ behavior: "smooth" });
    const textarea = replyFormRef.current?.querySelector("input, textarea");
    if (textarea) setTimeout(() => textarea.focus(), 300);
  };

  const toggleReplyLike = async (reply) => {
    const wasLiked = reply.liked;
    updateReply(reply.id, { liked: !wasLiked, likes: wasLiked ? reply.likes - 1 : reply.likes + 1 });
    const req = wasLiked
      ? apiDelete(`/api/v1/communities/threads/${reply.id}/likes`)
      : apiPost(`/api/v1/communities/threads/${reply.id}/likes`);
    req.catch(() => updateReply(reply.id, { liked: wasLiked, likes: reply.likes }));
  };

  const handleSubReplyAdded = (parentReplyId, newSubReply) => {
    setReplies((prev) => prev.map((r) =>
      r.id === parentReplyId
        ? { ...r, sub_replies: [...(r.sub_replies || []), newSubReply], replies: (r.replies || 0) + 1 }
        : r
    ));
  };

  const userInitial = currentUser?.name?.charAt(0) || currentUser?.username?.charAt(0) || "?";

  return (
    <AppLayout>
      <LeftSidebar
        onCreatePost={() => {}}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      <MainArea>
        <FeedColumn>
          {/* Mobile Header */}
          <MobileHeader>
            <BackButton onClick={() => window.history.back()}>
              <ArrowLeft size={20} />
            </BackButton>
            <MobileTitle>Post</MobileTitle>
          </MobileHeader>

          {/* Desktop Header */}
          <HeaderBar>
            <BackButton onClick={() => window.history.back()}>
              <ArrowLeft size={20} />
            </BackButton>
            <HeaderTitle>Post</HeaderTitle>
          </HeaderBar>

          {loading && <LoadingState>Loading...</LoadingState>}
          {error && <ErrorState>{error}</ErrorState>}

          {!loading && !error && thread && (
            <>
              {/* Thread */}
              <ThreadContainer>
                <ThreadContent>
                  <AvatarCircle
                    href={`/communities/profiles/${thread.author.slug || thread.author.handle.replace("@", "")}`}
                  >
                    {thread.author.avatar_url ? (
                      <img
                        src={thread.author.avatar_url}
                        alt={thread.author.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <span>{thread.author.name?.charAt(0)}</span>
                    )}
                  </AvatarCircle>
                  <ThreadMainContent>
                    <AuthorHeader>
                      <AuthorInfo>
                        <AuthorNameRow>
                          <AuthorName
                            href={`/communities/profiles/${thread.author.slug || thread.author.handle.replace("@", "")}`}
                          >
                            {thread.author.name}
                          </AuthorName>
                          {thread.author.role && (
                            <RoleBadge>{thread.author.role.split(" ")[0]}</RoleBadge>
                          )}
                        </AuthorNameRow>
                        <AuthorHandle>{thread.author.handle}</AuthorHandle>
                      </AuthorInfo>
                      <MoreButton>
                        <MoreHorizontal size={18} />
                      </MoreButton>
                    </AuthorHeader>
                  </ThreadMainContent>
                </ThreadContent>

                <ThreadText>{renderContent(thread.content)}</ThreadText>
                {thread.poll && (
                  <PollDisplay
                    poll={thread.poll}
                    threadId={thread.id}
                    authorId={thread.author?.id}
                    onPollUpdate={(updatedPoll) => setThread((t) => ({ ...t, poll: updatedPoll }))}
                  />
                )}
                <ThreadTimestamp title={thread.created_at_iso ? new Date(thread.created_at_iso).toLocaleString() : undefined}>{thread.timestamp}</ThreadTimestamp>

                <ThreadStats>
                  <StatItem>
                    <strong>{thread.reposts}</strong> Reposts
                  </StatItem>
                  <StatItem>
                    <strong>{thread.likes}</strong> Likes
                  </StatItem>
                  <StatItem>
                    <strong>{thread.replies}</strong> Replies
                  </StatItem>
                </ThreadStats>
              </ThreadContainer>

              <ActionButtons>
                <ActionButton onClick={scrollToReplyForm}>
                  <MessageCircle size={20} />
                  {thread.replies > 0 && <span>{thread.replies}</span>}
                </ActionButton>
                <ActionButton $isActive={thread.reposted} onClick={toggleThreadRepost}>
                  <Share2 size={20} />
                  {thread.reposts > 0 && <span>{thread.reposts}</span>}
                </ActionButton>
                <ActionButton
                  $isActive={thread.liked}
                  $activeColor="#ef4444"
                  onClick={toggleThreadLike}
                >
                  <Heart size={20} fill={thread.liked ? "#ef4444" : "none"} />
                  {thread.likes > 0 && <span>{thread.likes}</span>}
                </ActionButton>
                <ActionButton $isActive={thread.bookmarked} onClick={toggleThreadBookmark}>
                  <Bookmark size={20} fill={thread.bookmarked ? "currentColor" : "none"} />
                </ActionButton>
              </ActionButtons>

              {/* Reply Form */}
              <div ref={replyFormRef}>
                <ReplyForm
                  threadId={thread.id}
                  onReplied={(newReply) => setReplies((prev) => [...prev, newReply])}
                  userInitial={userInitial}
                />
              </div>

              {/* Replies */}
              {replies.map((reply) => (
                <ReplyCard
                  key={reply.id}
                  reply={reply}
                  onLike={() => toggleReplyLike(reply)}
                  onSubReplyAdded={handleSubReplyAdded}
                  userInitial={userInitial}
                />
              ))}
            </>
          )}
        </FeedColumn>

        <RightSidebar>
          <TrendingPanel />
        </RightSidebar>
      </MainArea>
    </AppLayout>
  );
}
